/**
 * The geometry of the month's bar (`<MonthChart>`): the month's items side by
 * side as segments of one horizontal bar, against the month's budget, or on
 * their own for a viewer who does not see the budget (an admin), whose items
 * carry no hours either: their bar runs on each item's share of the month.
 *
 * Every x is a percentage of the bar's width, so the chart sets SVG
 * attributes rather than inline styles.
 */

/** What the bar reads of one of the month's items. */
export interface MonthChartItem {
  id: string;
  title: string;
  status: string;
  /**
   * What the item counts against the month, in hours; absent for a viewer
   * who does not see the hours (an admin).
   */
  used_hours?: number | null;
  /** Its part of what the month's items use together, from 0 to 1, for every viewer. */
  share: number;
}

/** One item's segment: done work in De Ceuster blue, the rest in the light blue. */
export interface MonthSegment {
  id: string;
  title: string;
  /** Its hours; null on a bar that runs on shares, for a viewer who does not see the hours. */
  hours: number | null;
  tone: "done" | "planned";
  /** Its left edge and width, in % of the bar. */
  x: number;
  width: number;
  /**
   * Its part past the budget line, in % of the bar, which the chart stripes;
   * null for a segment within the budget. A segment that crosses the line
   * has only its part past it here.
   */
  over: { x: number; width: number } | null;
}

export interface MonthChartGeometry {
  segments: MonthSegment[];
  /** Where two segments meet, in % of the bar: a 2 px white gap goes there. */
  gaps: number[];
  /**
   * Whether the bar runs on the items' hours; false when they carry none (an
   * admin's month), and it runs on their shares.
   */
  byHours: boolean;
  /** The hours of every segment together, or their shares on a bar that runs on shares. */
  total: number;
  /** What the bar's full width stands for: the budget, or the total when that is more. */
  axisMax: number;
  /** The budget line's place, in % of the bar; null without a budget. */
  budgetX: number | null;
  /** Whether the month holds more than its budget: then the legend names the stripes. */
  overBudget: boolean;
  /** The tones of the segments with a part past the budget, done first: the legend's striped swatches. */
  overTones: MonthSegment["tone"][];
}

/**
 * The bar for a month: its items in the order they came (the server sends the
 * done ones first, then the rest by rank), each as wide as its hours, on an
 * axis that runs to the budget or to the total when the month is over its
 * budget. An item without hours takes no room. What lies past the budget is
 * each segment's `over` part, counted in hours, so a segment that ends right
 * on the line has none. Without a budget (null, for an admin) the axis runs
 * to the total, and nothing is past a line. When the items carry no hours
 * (an admin's month) each is as wide as its share, on an axis that runs to
 * the shares together, so the bar keeps its proportions without a figure.
 */
export function monthChart(
  budgetHours: number | null,
  items: readonly MonthChartItem[],
): MonthChartGeometry {
  const byHours = items.every((item) => typeof item.used_hours === "number");
  const size = (item: MonthChartItem) => (byHours ? (item.used_hours ?? 0) : item.share);
  const counted = items.filter((item) => size(item) > 0);
  const total = counted.reduce((sum, item) => sum + size(item), 0);
  const axisMax = byHours ? Math.max(budgetHours ?? 0, total, 1) : total || 1;
  const scale = (amount: number) => (amount / axisMax) * 100;

  // Without a budget, nothing lies past it.
  const budget =
    budgetHours === null || !byHours ? Number.POSITIVE_INFINITY : Math.max(0, budgetHours);
  let at = 0;
  const segments = counted.map((item): MonthSegment => {
    const amount = size(item);
    const end = at + amount;
    const overFrom = Math.max(at, budget);
    const segment: MonthSegment = {
      id: item.id,
      title: item.title,
      hours: byHours ? amount : null,
      tone: item.status === "done" ? "done" : "planned",
      x: scale(at),
      width: scale(amount),
      over: end > overFrom ? { x: scale(overFrom), width: scale(end - overFrom) } : null,
    };
    at = end;
    return segment;
  });
  const tones = new Set(segments.filter((segment) => segment.over).map((segment) => segment.tone));

  return {
    segments,
    gaps: segments.slice(1).map((segment) => segment.x),
    byHours,
    total,
    axisMax,
    budgetX: budgetHours === null || !byHours ? null : scale(budget),
    overBudget: total > budget,
    overTones: (["done", "planned"] as const).filter((tone) => tones.has(tone)),
  };
}

/**
 * How a label at an x of the bar lines up: from its start near the left
 * edge, to its end near the right edge, centred anywhere else, so it never
 * runs past the bar.
 */
export function labelAnchor(x: number): "start" | "middle" | "end" {
  if (x <= 15) {
    return "start";
  }
  return x >= 85 ? "end" : "middle";
}
